import { Home } from "@mui/icons-material";
import * as MUI from "@mui/material";
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import GoBack from "../../../components/buttons/GoBack";
import DetailCard from "../../../components/display/DetailCard";
import PetsGrid from "../../../components/display/PetsGrid";
import SimpleAlert from "../../../components/display/SimpleAlert";
import UserHint from "../../../components/display/UserHint";
import UserVerification from "../../../components/display/UserVerification";
import Skeletons from "../../../components/loader/Skeletons";
import CustomDialog from "../../../components/wrappers/CustomDrawer";
import PageContentWrapper from "../../../components/wrappers/PageContentWrapper";
import TopNavigator from "../../../components/wrappers/TopNavigator";
import { GlobalDataContext } from "../../../contexts/GlobalData";
import ToggleDrawer from "../../../contexts/ToggleDrawer";
import { CREATE_PET } from "../../../enums/PermissionTitles";
import { CLINIC_USERS, PETS } from "../../../enums/UrlPaths";
import usePrimaryColor from "../../../hooks/usePrimaryColor";
import useUser from "../../../hooks/useUser";
import SingleUserEditing from "../../master/users/components/SingleUserEditing";
import AddPetForm from "../../pets/form/AddPetForm";
import UserInformationView from "../../profile/component/UserInformationView";
import { ErrorMessage } from "../../saps/components/display/NotFound";
import LoginHistory from "./components/LoginHistory";
import UserActionsMenu from "./components/UserActionsMenu";
import { useGetSingleClinicUser } from "./hooks/useGetEmployees";

export default function SingleUserView() {
  const { id } = useParams();
  const [isFlipped, setIsFlipped] = React.useState(false);

  const { roles } = useUser();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const { employeeObj, petObj } = useGetSingleClinicUser(id);
  const { t } = useTranslation();

  const dataToParse = roles?.master
    ? employeeObj?.data
    : employeeObj?.data?.userDetails;

  const userInformation = UserInformationView(dataToParse, roles?.master);

  const isActive = employeeObj?.data?.active;
  const isBlocked = employeeObj?.data?.blocked;

  function handleNavigation() {
    setIsFlipped((prev) => !prev);
  }

  const color = usePrimaryColor();
  const hasPermission = isPermissionAllowed(CREATE_PET);
  const isEmployeeFetchValid = Boolean(
    !employeeObj?.error && employeeObj?.data,
  );
  const canEdit = roles.master || (roles.admin && !isActive);

  if (!employeeObj.isLoading && !isEmployeeFetchValid) {
    return (
      <PageContentWrapper>
        <ErrorMessage
          title={t("ErrorFetchingClinicUserData")}
          subtitle={t("ClinicUserNotFoundSubtitle")}
        />
      </PageContentWrapper>
    );
  }

  return (
    <PageContentWrapper>
      <TopNavigator>
        <GoBack url={CLINIC_USERS} />
        <MUI.Stack
          width={"100%"}
          display={"flex"}
          justifyContent={"end"}
          alignItems={"center"}
          direction={"row"}
        >
          {isEmployeeFetchValid && (
            <MUI.Stack
              direction={"row"}
              alignItems={"center"}
              spacing={2}
              justifyContent={"space-between"}
            >
              <UserVerification data={dataToParse} />
              <UserActionsMenu
                email={dataToParse?.email}
                userId={id}
                active={isActive}
                blocked={isBlocked}
                role={employeeObj?.data?.role}
                onEdit={handleNavigation}
                canEdit={canEdit}
                editLabel="EditUser"
                editDescription="EditUserDetails"
                menuTitle="UserActions"
                showOpenConversation
              />
            </MUI.Stack>
          )}
        </MUI.Stack>
      </TopNavigator>
      {isFlipped ? (
        // BACK SIDE
        <SingleUserEditing setViewEditForm={handleNavigation} />
      ) : (
        // FRONT SIDE
        <Fragment>
          <IsActive isActive={isActive} isLoading={employeeObj.isLoading} />

          {employeeObj.isLoading ? (
            <Skeletons numOfSkeletons={6} height={50} />
          ) : (
            <DetailCard
              valueArray={userInformation}
              grid
              gridCount={2}
              elevation={4}
            />
          )}
          <LoginHistory loginHistory={dataToParse?.loginHistory} />
          {employeeObj?.data?.createdAt ? (
            <UserHint
              hint={`${t("AccountCreatedAt")} ${employeeObj?.data.createdAt}`}
            />
          ) : null}

          {employeeObj?.data?.lastLogin ? (
            <UserHint
              hint={`${t("LastLogin")} ${employeeObj?.data.lastLogin}`}
            />
          ) : null}

          {petObj.petLoading ? (
            <MUI.Container sx={{ mt: 5 }}>
              <MUI.LinearProgress />
            </MUI.Container>
          ) : isEmployeeFetchValid && petObj.pet?.length > 0 ? (
            <MUI.Paper sx={{ p: 2, mt: 5, pb: 5 }}>
              <MUI.Typography variant="h5" mb={5}>
                {t("UsersPets")}
              </MUI.Typography>

              <PetsGrid
                hasPermission={hasPermission}
                displayForm={true}
                mutationKey={PETS + "user" + id}
                petsArray={petObj.pet}
                loading={petObj.isLoading}
                error={petObj.error}
                ownerId={id}
              />
            </MUI.Paper>
          ) : hasPermission && isEmployeeFetchValid ? (
            <MUI.Container
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 5,
              }}
            >
              <MUI.Stack
                direction={"row"}
                spacing={3}
                sx={{
                  borderRadius: 8,
                  p: 2,
                  bgcolor: color,
                }}
              >
                <MUI.Stack
                  bgcolor={"white"}
                  padding={2}
                  borderRadius={10}
                  boxShadow={5}
                  alignItems={"center"}
                >
                  <Home className="Mui-fullscreen-icon" />
                </MUI.Stack>

                <MUI.Stack spacing={2}>
                  <MUI.Typography
                    color={"white"}
                    textAlign={"center"}
                    variant="h5"
                  >
                    {t("UserHasNoPets")}
                  </MUI.Typography>

                  <ToggleDrawer>
                    <CustomDialog
                      buttonMessage={t("AddPet")}
                      buttonSize={"large"}
                      variant={"contained"}
                      testId="user.pets.add-btn"
                    >
                      <AddPetForm mutationKey={PETS + id} />
                    </CustomDialog>
                  </ToggleDrawer>
                </MUI.Stack>
              </MUI.Stack>
            </MUI.Container>
          ) : null}
        </Fragment>
      )}
    </PageContentWrapper>
  );
}

function IsActive({ isActive, isLoading }) {
  if (isLoading) return null;

  if (isActive === false) {
    return (
      <SimpleAlert
        message={"ThisUserIsInactive"}
        severity={"warning"}
        testId="user.deactivated-alert"
      />
    );
  }

  return null;
}
